'use client'

import { ArrowLeft, Briefcase, Key, Sparkles, UtensilsCrossed } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import { useB2bFormMutation } from '@/api/b2b/b2b-form-mutation'
import { Button } from '@/components/button'
import { Input } from '@/components/input/input'
import {
    GooglePlacesRestaurantsSearch,
    IGoogleAddressOption,
} from '@/features/google-places/google-places-restaurants-search'
import { homeSizeStyles } from '@/features/home/utils/home-size'
import { cn } from '@/utils/styles/cn'

const stepIcons = [Briefcase, Key, UtensilsCrossed]
const stepKeys = ['step1', 'step2', 'step3'] as const

export const B2bCodesPage = () => {
    const t = useTranslations('for-companies.codes')
    const [companyName, setCompanyName] = useState('')
    const [email, setEmail] = useState('')
    const [employeeCount, setEmployeeCount] = useState('')
    const [addressOption, setAddressOption] = useState<IGoogleAddressOption | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const { mutateAsync: submitB2bForm } = useB2bFormMutation()

    const disabled = useMemo(() => {
        return !companyName.trim() || !email.trim() || !employeeCount.trim() || !addressOption
    }, [companyName, email, employeeCount, addressOption])

    const onSubmit = useCallback(async () => {
        if (disabled || !addressOption) return
        setIsSubmitting(true)
        try {
            await submitB2bForm({
                body: {
                    type: 'discount-codes',
                    companyName,
                    email,
                    employeeCount: Number(employeeCount),
                    contact: {
                        firstname: companyName,
                        email,
                        phone: '',
                    },
                    company: {
                        name: companyName,
                        address: addressOption.label,
                        zip: addressOption.value.zip,
                        city: addressOption.value.city,
                        country: addressOption.value.country,
                    },
                },
            })
            setIsSubmitted(true)
        } finally {
            setIsSubmitting(false)
        }
    }, [disabled, companyName, email, employeeCount, addressOption, submitB2bForm])

    const onFormKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                onSubmit()
            }
        },
        [onSubmit]
    )

    return (
        <div className={cn('mx-auto flex w-full flex-col items-center', homeSizeStyles)}>
            <div className="flex w-full flex-col items-center px-4 py-8 md:px-0 md:py-16">
                {/* Back link */}
                <div className="mb-12 w-full max-w-[672px]">
                    <Button
                        href={{ pattern: '/for-companies', params: undefined }}
                        styleType="unstyled"
                        shape="pill"
                    >
                        <span className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900">
                            <ArrowLeft className="size-4" />
                            {t('backLink')}
                        </span>
                    </Button>
                </div>

                {/* Logo + Title */}
                <div className="mb-12 flex flex-col items-center">
                    <div className="mb-6 flex size-16 items-center justify-center rounded-l bg-gray-900">
                        <span className="text-2xl font-extrabold text-white">N</span>
                    </div>

                    <h1 className="mb-4 text-center text-4xl font-extrabold text-gray-900 md:text-5xl">
                        {t('title')}
                    </h1>

                    <p className="max-w-md text-center text-gray-500">{t('subtitle')}</p>
                </div>

                {/* Steps */}
                <div className="mb-12 grid w-full max-w-[672px] grid-cols-1 gap-4 md:grid-cols-3">
                    {stepKeys.map((key, index) => {
                        const Icon = stepIcons[index]
                        return (
                            <div
                                key={key}
                                className="flex flex-col items-center rounded-l border border-gray-200 bg-white p-6 text-center shadow-xs"
                            >
                                <span className="mb-3 text-sm font-bold tracking-widest text-gray-400">
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <div className="mb-4 flex size-12 items-center justify-center rounded-m bg-[#2DD282]/10">
                                    <Icon className="size-5 text-[#2DD282]" />
                                </div>

                                <h3 className="mb-2 text-lg font-bold text-gray-900">
                                    {t(`steps.${key}.title`)}
                                </h3>

                                <p className="text-sm text-gray-500">
                                    {t(`steps.${key}.description`)}
                                </p>
                            </div>
                        )
                    })}
                </div>

                {/* Form */}
                <div className="w-full max-w-[672px] rounded-l border border-gray-200 bg-white p-8 shadow-xs">
                    {isSubmitted ? (
                        <div className="flex flex-col items-center py-8">
                            <div className="mb-4 flex size-14 items-center justify-center rounded-l bg-[#2DD282]/15">
                                <Sparkles className="size-6 text-[#2DD282]" />
                            </div>
                            <h3 className="mb-2 text-2xl font-bold text-gray-900">
                                {t('success.title')}
                            </h3>
                            <p className="text-center text-gray-500">{t('success.description')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6 flex items-center gap-3">
                                <div className="flex size-10 items-center justify-center rounded-m bg-[#2DD282]/15">
                                    <Sparkles className="size-5 text-[#2DD282]" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {t('formTitle')}
                                </h2>
                            </div>

                            <div onKeyDown={onFormKeyDown} className="flex flex-col gap-5">
                                <Input
                                    label={t('companyName.label')}
                                    value={companyName}
                                    onValueChange={setCompanyName}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={t('companyName.placeholder')}
                                    labeledContainerClassName="w-full"
                                />

                                <Input
                                    autoComplete="email"
                                    label={t('email.label')}
                                    value={email}
                                    onValueChange={setEmail}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={t('email.placeholder')}
                                    labeledContainerClassName="w-full"
                                />

                                <Input
                                    label={t('employeeCount.label')}
                                    value={employeeCount}
                                    onValueChange={setEmployeeCount}
                                    styleType="grey"
                                    inputClassName="min-h-12 placeholder:text-[14px]"
                                    placeholder={t('employeeCount.placeholder')}
                                    labeledContainerClassName="w-full"
                                />

                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t('companyAddress.label')}
                                    </span>
                                    <GooglePlacesRestaurantsSearch
                                        onSelect={setAddressOption}
                                        placeholder={t('companyAddress.placeholder')}
                                        inputClassName="min-h-12 min-w-full placeholder:text-[14px]"
                                        containerClassName="w-full"
                                    />
                                    {addressOption && (
                                        <p className="mt-1 text-sm text-gray-500">
                                            {addressOption.label}
                                        </p>
                                    )}
                                </div>

                                <Button
                                    disabled={disabled}
                                    onClick={onSubmit}
                                    isLoading={isSubmitting}
                                    setIsLoading={setIsSubmitting}
                                    styleType="primary"
                                    shape="pill"
                                    height="54px"
                                    width="100%"
                                >
                                    {t('submitButton')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
